// Import assets from a CSV (CR_20260920_Asset_Register phase C, §5.2) — managers (Q4). Three steps on one page:
// download the template or drop a file; a preview that checks every row with the server's own rules before anything
// is sent (@fleetspoke/core/assetImport); Import — one import_assets call that writes every row or none (Q3). The file
// never leaves the browser until Import.
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  type ImportIssue,
  type ImportRow,
  importTemplate,
  readImport,
  type ReadResult,
  validateImport,
} from '@fleetspoke/core/assetImport';
import { type DragEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import { Page } from '../components/Page';
import { activeSites, useCategories, useSites } from '../data/queries';
import { type ImportResult, takenCodes, useImportAssets } from '../data/register';
import { download } from '../lib/labels';

const PREVIEW_ROWS = 50;

/** File → text through FileReader (Blob.text() is missing from some embedded browsers and from jsdom). */
function readText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('could not read the file'));
    reader.readAsText(file);
  });
}

interface Loaded {
  name: string;
  read: ReadResult;
  issues: ImportIssue[];
}

export function AssetImportPage() {
  const { t, i18n } = useTranslation();
  const categories = useCategories();
  const sites = useSites();
  const importer = useImportAssets();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [checking, setChecking] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const num = (n: number) => new Intl.NumberFormat(i18n.language).format(n);

  const load = async (file: File) => {
    importer.reset();
    setReadError(null);
    setChecking(true);
    try {
      const read = readImport(await readText(file));
      const taken = await takenCodes(read.rows.map((r) => r.asset_code));
      const issues = validateImport(read.rows, {
        categories: (categories.data ?? []).map((c) => c.name),
        sites: activeSites(sites.data).map((s) => s.name),
        taken,
      });
      setLoaded({ name: file.name, read, issues });
    } catch (e) {
      setReadError((e as Error).message);
      setLoaded(null);
    } finally {
      setChecking(false);
    }
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) void load(file);
  };

  // the server's answer, when it found something the preview did not (a code registered meanwhile)
  const server: ImportResult | undefined = importer.data;
  const issues: ImportIssue[] =
    server && !server.ok ? (server.errors as ImportIssue[]) : (loaded?.issues ?? []);
  const byRow = new Map<number, ImportIssue[]>();
  for (const i of issues) byRow.set(i.row, [...(byRow.get(i.row) ?? []), i]);
  const rows = loaded?.read.rows ?? [];
  const problems = loaded?.read.problems ?? [];
  // an unknown column is ignored, not fatal; no code column, no rows or too many rows stop the import
  const fatal = problems.filter((p) => p.kind !== 'unknown_column');
  const blocked = !loaded || checking || !rows.length || fatal.length > 0 || issues.length > 0;
  const message = (i: ImportIssue) => t(`import.err_${i.code}`, { value: i.value ?? '' });

  return (
    <Page title={t('import.title')}>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {t('import.intro')}
      </Typography>

      {/* 1 — the template, or a file */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          onClick={() => download('assets-import-template.csv', importTemplate(), 'text/csv')}
          data-testid="import-template"
        >
          {t('import.template')}
        </Button>
        <Paper
          variant="outlined"
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          sx={{
            flex: 1,
            p: 2,
            borderStyle: 'dashed',
            borderColor: dragging ? 'primary.main' : undefined,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <UploadFileIcon color="action" />
          <Typography sx={{ flex: 1 }}>{loaded?.name ?? t('import.drop')}</Typography>
          {/* the checks need the categories and places first */}
          <Button
            component="label"
            variant="contained"
            disabled={checking || !categories.data || !sites.data}
          >
            {t('import.choose')}
            <input
              hidden
              type="file"
              accept=".csv,text/csv"
              data-testid="import-file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void load(file);
                e.target.value = '';
              }}
            />
          </Button>
        </Paper>
      </Stack>
      {readError ? <Alert severity="error">{readError}</Alert> : null}
      {checking ? <Alert severity="info">{t('import.checking')}</Alert> : null}

      {/* 2 — the preview */}
      {loaded && !checking ? (
        <Box data-testid="import-preview">
          {problems.map((p) => (
            <Alert
              key={`${p.kind}:${p.column ?? ''}`}
              severity={p.kind === 'unknown_column' ? 'warning' : 'error'}
              sx={{ mb: 1 }}
            >
              {t(`import.problem_${p.kind}`, { column: p.column ?? '' })}
            </Alert>
          ))}
          <Typography sx={{ mb: 1 }} data-testid="import-summary">
            {issues.length
              ? t('import.summary_errors', { rows: num(rows.length), bad: num(byRow.size) })
              : t('import.summary_ok', { rows: num(rows.length) })}
          </Typography>
          {rows.length ? (
            <Box sx={{ overflowX: 'auto', mb: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t('assets.code')}</TableCell>
                    <TableCell>{t('assets.category')}</TableCell>
                    <TableCell>{t('assets.description')}</TableCell>
                    <TableCell>{t('assets.custody_site')}</TableCell>
                    <TableCell>{t('import.col_check')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* rows with errors first, so a 500-row file shows what to fix without scrolling */}
                  {rows
                    .map((r, i) => ({ r, n: i + 1 }))
                    .sort((a, b) => Number(byRow.has(b.n)) - Number(byRow.has(a.n)) || a.n - b.n)
                    .slice(0, PREVIEW_ROWS)
                    .map(({ r, n }: { r: ImportRow; n: number }) => (
                      <TableRow key={n} data-testid={`import-row-${n}`}>
                        <TableCell>{n}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{r.asset_code || '—'}</TableCell>
                        <TableCell>{r.category || '—'}</TableCell>
                        <TableCell>{r.description || '—'}</TableCell>
                        <TableCell>{r.custody_site || '—'}</TableCell>
                        <TableCell>
                          {byRow.has(n) ? (
                            <Stack spacing={0.5}>
                              {byRow.get(n)!.map((i) => (
                                <Chip
                                  key={`${i.code}:${i.field}`}
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  label={message(i)}
                                />
                              ))}
                            </Stack>
                          ) : (
                            <Chip
                              size="small"
                              color="success"
                              variant="outlined"
                              label={t('import.row_ok')}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          ) : null}
          {rows.length > PREVIEW_ROWS ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('import.more_rows', {
                count: rows.length - PREVIEW_ROWS,
                n: num(rows.length - PREVIEW_ROWS),
              })}
            </Typography>
          ) : null}

          {/* 3 — import */}
          {server?.ok ? (
            <Alert
              severity="success"
              data-testid="import-done"
              action={
                <Button color="inherit" component={RouterLink} to="/assets?registered=7">
                  {t('import.see_them')}
                </Button>
              }
            >
              {t('import.done', { count: server.created, n: num(server.created) })}
            </Alert>
          ) : (
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                disabled={blocked || importer.isPending}
                onClick={() => importer.mutate(rows)}
                data-testid="import-submit"
              >
                {t('import.submit', { count: rows.length, n: num(rows.length) })}
              </Button>
              {issues.length ? (
                <Typography variant="body2" color="error">
                  {t('import.fix_first')}
                </Typography>
              ) : null}
            </Stack>
          )}
          {importer.error ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {(importer.error as Error).message}
            </Alert>
          ) : null}
        </Box>
      ) : null}
    </Page>
  );
}
